import * as ToggleGroup from '@radix-ui/react-toggle-group'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { getStore } from '@/store'

import {
	addressCityAtom,
	addressCountryAtom,
	addressPostalCodeAtom,
	addressStateAtom,
	addressStreetAtom,
	addressUrlAtom,
} from '../LocationForm/addressAtoms'
import { TAddressFields, TAddressType } from '../LocationForm/addressSchema'
import { AddressTypeButton } from '../LocationForm/AddressTypeButton'
import { PromptDialog } from '../LocationForm/Dialogs/PromptDialog'
import { FullAddressFields } from '../LocationForm/Fields/FullAddressFields'
import { UrlAddressField } from '../LocationForm/Fields/UrlAddressField'

export type TAddressFormSectionProps<T extends TAddressFields> = {
	form: UseFormReturn<T>
}

export const AddressSection = <T extends TAddressFields>({
	form,
}: TAddressFormSectionProps<T>) => {
	const t = useTranslations()

	const [isPromptDialogOpen, setIsPromptDialogOpen] = useState(false)
	const [addressType, setAddressType] = useState<TAddressType>('manual')
	const hasAddressUrl = !!useAtomValue(addressUrlAtom)

	useEffect(() => {
		if (addressType !== 'url' && hasAddressUrl) {
			setAddressType('url')
		}
	}, [addressType, hasAddressUrl])

	const resetStreet = useResetAtom(addressStreetAtom)
	const resetPostalCode = useResetAtom(addressPostalCodeAtom)
	const resetCity = useResetAtom(addressCityAtom)
	const resetState = useResetAtom(addressStateAtom)
	const resetCountry = useResetAtom(addressCountryAtom)
	const resetAddressUrl = useResetAtom(addressUrlAtom)

	const handleConfirmSwitchType = () => {
		const newType = addressType === 'manual' ? 'url' : 'manual'
		setAddressType(newType)
		if (newType === 'url') {
			resetStreet()
			resetPostalCode()
			resetCity()
			resetState()
			resetCountry()
		} else {
			resetAddressUrl()
		}
	}

	const { trigger } = form

	const handleClickAddressType = () => {
		trigger()
		if (addressType === 'url') {
			const isAddressUrlEmpty = !getStore().get(addressUrlAtom)?.length
			if (isAddressUrlEmpty) {
				setAddressType('manual')
			} else {
				setIsPromptDialogOpen(true)
			}
		} else if (addressType === 'manual') {
			const isFullAddressEmpty =
				!getStore().get(addressStreetAtom)?.length &&
				!getStore().get(addressPostalCodeAtom)?.length &&
				!getStore().get(addressCityAtom)?.length &&
				!getStore().get(addressStateAtom)?.length &&
				!getStore().get(addressCountryAtom)?.length
			if (isFullAddressEmpty) {
				setAddressType('url')
			} else {
				setIsPromptDialogOpen(true)
			}
		}
	}

	const addressTypeButtons = (
		<ToggleGroup.Root
			type="single"
			value={addressType}
			onValueChange={(value) => {
				if (value) {
					setAddressType(value as TAddressType)
				}
			}}
			asChild
		>
			<div className="mb-6 flex w-full flex-wrap gap-4">
				<AddressTypeButton
					value="manual"
					label={t(
						'generator__content_form_section__about__address_section__manual_button',
					)}
					isSelected={addressType === 'manual'}
					onClick={handleClickAddressType}
				/>
				<AddressTypeButton
					value="url"
					label={t(
						'generator__content_form_section__about__address_section__url_button',
					)}
					isSelected={addressType === 'url'}
					onClick={handleClickAddressType}
				/>
			</div>
		</ToggleGroup.Root>
	)

	return (
		<>
			<PromptDialog
				isOpen={isPromptDialogOpen}
				setIsOpen={setIsPromptDialogOpen}
				onConfirm={handleConfirmSwitchType}
			/>
			<div>
				{addressTypeButtons}
				{addressType === 'manual' ? <FullAddressFields /> : <UrlAddressField />}
			</div>
		</>
	)
}
